import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
    GET_PROJECT_HIERARCHIES,
    GET_ALL_ZONES_IN_PROJECT,
} from '@api/queries/projects';
import { useQueryConnection } from '@models/queries/useQueryConnection';
import { type Maybe } from '@models/Maybe';

interface HierarchyBreadcrumb {
    pathname: string;
    name: string;
    isLast: boolean;
}

enum HierarchyType {
    SYSTEM = 'system',
    ZONE = 'zone',
}

interface HierarchyNode {
    id: string;
    name: string;
}

interface SystemNode extends HierarchyNode {
    parentHierarchy?: Maybe<{ id: string; name: string }>;
}

interface ZoneNode extends HierarchyNode {
    parentZone?: Maybe<{ id: string; name: string }>;
}

function buildAncestry<T extends HierarchyNode>(
    nodeId: string,
    nodes: T[],
    getParentId: (node: T) => string | undefined,
): T[] {
    const nodeMap = new Map<string, T>();
    nodes.forEach((node) => nodeMap.set(node.id, node));

    const visited = new Set<string>();
    const ancestry: T[] = [];
    let current = nodeMap.get(nodeId);

    while (current && !visited.has(current.id)) {
        visited.add(current.id);
        ancestry.unshift(current);
        const parentId = getParentId(current);
        current = parentId ? nodeMap.get(parentId) : undefined;
    }

    return ancestry;
}

export function useHierarchyBreadcrumbs(): HierarchyBreadcrumb[] | null {
    const location = useLocation();
    const { pid, sid, zid } = useParams<{
        pid: string;
        sid: string;
        zid: string;
    }>();

    const hierarchyType = useMemo((): HierarchyType | null => {
        if (location.pathname.includes('/systems-and-assets/') && sid) {
            return HierarchyType.SYSTEM;
        }
        if (location.pathname.includes('/security-zones-assets/') && zid) {
            return HierarchyType.ZONE;
        }
        return null;
    }, [location.pathname, sid, zid]);

    const systemsQuery = useQueryConnection(
        GET_PROJECT_HIERARCHIES,
        { pid: pid ?? '' },
        { skip: !pid || hierarchyType !== HierarchyType.SYSTEM },
    );

    const zonesQuery = useQueryConnection(
        GET_ALL_ZONES_IN_PROJECT,
        { pid: pid ?? '' },
        { skip: !pid || hierarchyType !== HierarchyType.ZONE },
    );

    const breadcrumbs = useMemo((): HierarchyBreadcrumb[] | null => {
        if (!pid || !hierarchyType) return null;

        if (hierarchyType === HierarchyType.SYSTEM && sid) {
            const systems =
                (systemsQuery.nodes[0]?.projectHierarchies as SystemNode[]) ??
                [];
            if (!systems.length) return null;

            const ancestry = buildAncestry(
                sid,
                systems,
                (node) => node.parentHierarchy?.id,
            );
            if (!ancestry.length) return null;

            const basePath = `/projects/${pid}/systems-and-assets`;

            return ancestry.map((system, index) => ({
                pathname: `${basePath}/${system.id}`,
                name: system.name,
                isLast: index === ancestry.length - 1,
            }));
        }

        if (hierarchyType === HierarchyType.ZONE && zid) {
            const zones = (zonesQuery.nodes as ZoneNode[]) ?? [];
            if (!zones.length) return null;

            const ancestry = buildAncestry(
                zid,
                zones,
                (node) => node.parentZone?.id,
            );
            if (!ancestry.length) return null;

            const basePath = `/projects/${pid}/security-zones-assets`;

            return ancestry.map((zone, index) => ({
                pathname: `${basePath}/${zone.id}`,
                name: zone.name,
                isLast: index === ancestry.length - 1,
            }));
        }

        return null;
    }, [pid, hierarchyType, sid, zid, systemsQuery.nodes, zonesQuery.nodes]);

    return breadcrumbs;
}
